import { useDefaultHighlights } from "./../../constants/Table";
import { CustomRendererType } from "./../../types/Table";
import { MpColumn, MpTableType } from "@mp-react/table";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Colors } from "../../constants/Style";
import { useTableFilterTypes } from "../../utils/Table";
import { useLocalStorage } from "../../utils/LocalStorage";
import { StorageKeys } from "../../types/LocalStorage";

export const useGroupsAssignBenefitsTable = (): MpTableType => {
  const { t } = useTranslation();
  const highlights = useDefaultHighlights();
  const { keyWithSuffix } = useLocalStorage();

  const {
    getSelectFilter,
    getFilterNameSearchSelectFilter,
    numberRangeFilter,
  } = useTableFilterTypes();

  const benefitPlanNameFilter = useMemo(
    () => getFilterNameSearchSelectFilter("getBenefitPlanName"),
    [getFilterNameSearchSelectFilter]
  );

  const benefitPlanTypeFilter = useMemo(
    () => getFilterNameSearchSelectFilter("getBenefitPlanType"),
    [getFilterNameSearchSelectFilter]
  );

  const currentStatusFilter = useMemo(
    () =>
      getSelectFilter("currentStatus", [
        {
          label: t("status.status_off"),
          value: "off",
        },
        {
          label: t("status.status_flex"),
          value: "flex",
        },
        {
          label: t("status.status_on"),
          value: "on",
        },
      ]),
    [getSelectFilter, t]
  );

  const columns = useMemo(
    (): MpColumn[] => [
      {
        label: t("common.plan_name"),
        key: "name",
        filter: benefitPlanNameFilter,
        width: 293,
        fixedHeadWidth: true,
      },
      {
        label: t("benefits.benefit_type"),
        key: "type",
        render: CustomRendererType.benefitTypeTranslation as any,
        filter: benefitPlanTypeFilter,
        width: 160,
        fixedHeadWidth: true,
      },
      {
        label: t("common.investment"),
        key: "investment",
        render: CustomRendererType.currency as any,
        sort: false,
        width: 122,
        fixedHeadWidth: true,
        filter: numberRangeFilter,
      },
      {
        label: t("table.previous_status"),
        key: "statuses",
        sort: false,
        render: CustomRendererType.entries as any,
      },
      {
        label: t("common.current_status"),
        key: "currentStatus",
        render: "statusToggle" as any,
        totals: false,
        sort: false,
        filter: currentStatusFilter,
        action: {
          slug: "toggleStatus",
          items: [
            {
              label: t("status.off"),
              value: "off",
              color: Colors.Default,
              textColor: Colors.TextSecondary,
            },
            {
              label: t("status.flex"),
              value: "flex",
              color: Colors.StatusYellow,
              textColor: Colors.White,
            },
            {
              label: t("status.on"),
              value: "on",
              color: Colors.StatusGreen,
              textColor: Colors.White,
            },
          ],
        },
        width: 209,
        fixedHeadWidth: true,
      },
    ],
    [
      benefitPlanNameFilter,
      benefitPlanTypeFilter,
      currentStatusFilter,
      t,
      numberRangeFilter,
    ]
  );

  return {
    columns,
    rowIdKey: "id",
    filterVariant: "head",
    disablePagination: true,
    highlights: highlights,
    storageIdentifier: keyWithSuffix(StorageKeys.groupsAssignBenefitsTable),
    cacheFilters: true,
    cacheSorting: true,
    clearColumnFilters: true,
  };
};
